import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { renderDuesyMarkdown } from '../duesy-markdown';
import { DuesyPage } from '../duesy-pages';
import { DuesyMessage, DuesyState, DuesyStateService } from '../duesy-state.service';

export const DUESY_TOPICS = [
  { label: 'Getting Started', icon: 'pi pi-compass', question: 'How do I get started with duesPlus?' },
  { label: 'Managing Members', icon: 'pi pi-users', question: 'How do I manage members?' },
  { label: 'Money & Transactions', icon: 'pi pi-wallet', question: 'How does money flow through duesPlus: deposits, invoices, funds requests, and expenses?' },
  { label: 'Reports & Exports', icon: 'pi pi-chart-bar', question: 'How do I run reports and export data?' },
];

const SCROLL_SLACK_PX = 40;

@Component({
  selector: 'app-duesy-panel',
  templateUrl: './duesy-panel.component.html',
  styleUrls: ['./duesy-panel.component.css'],
})
export class DuesyPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  @Output() minimizeRequest = new EventEmitter<void>();
  @Output() closeRequest = new EventEmitter<void>();
  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;
  @ViewChild('input') input?: ElementRef<HTMLTextAreaElement>;

  readonly topics = DUESY_TOPICS;
  readonly contactSupportUrl = environment.duesy.helpLinks.contactSupport;
  state!: DuesyState;
  page!: DuesyPage;
  draft = '';
  announcement = '';
  autoScroll = true;
  hasUnseen = false;
  listening = false;
  now = Date.now();
  readonly speechSupported: boolean;

  private readonly htmlCache = new Map<string, { content: string; html: SafeHtml }>();
  private recognition: any = null;
  private clock?: ReturnType<typeof setInterval>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    public duesy: DuesyStateService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {
    const w = window as any;
    this.speechSupported = Boolean(w.SpeechRecognition || w.webkitSpeechRecognition);
  }

  ngOnInit(): void {
    this.duesy.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      const grew = this.state && this.lastContent(state) !== this.lastContent(this.state);
      this.state = state;
      if (grew) { this.afterContentChange(); }
    });
    this.duesy.page$.pipe(takeUntil(this.destroy$)).subscribe((page) => (this.page = page));
    this.duesy.answerCompleted$.pipe(takeUntil(this.destroy$)).subscribe((text) => {
      // Announce whole answers once, not every streamed token.
      this.announcement = '';
      setTimeout(() => (this.announcement = `Duesy says: ${text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')}`));
    });
    // Relative timestamps tick once a minute, outside Angular to avoid churn.
    this.zone.runOutsideAngular(() => {
      this.clock = setInterval(() => this.zone.run(() => (this.now = Date.now())), 60000);
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.input?.nativeElement.focus();
      this.scrollToBottom();
    });
  }

  get remaining(): number {
    return this.state.maxMessageLength - this.draft.length;
  }

  get showCounter(): boolean {
    return this.remaining <= 100;
  }

  trackMessage(_: number, m: DuesyMessage): string {
    return m.localId;
  }

  html(message: DuesyMessage): SafeHtml {
    const cached = this.htmlCache.get(message.localId);
    if (cached && cached.content === message.content) { return cached.html; }
    // renderDuesyMarkdown sanitizes with DOMPurify (tags, attributes, and
    // URL schemes allow-listed); bypass only so Angular keeps target/rel.
    const html = this.sanitizer.bypassSecurityTrustHtml(renderDuesyMarkdown(message.content));
    this.htmlCache.set(message.localId, { content: message.content, html });
    return html;
  }

  relativeTime(date: Date): string {
    const seconds = Math.max(0, Math.round((this.now - new Date(date).getTime()) / 1000));
    if (seconds < 60) { return 'just now'; }
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) { return `${minutes} min ago`; }
    const hours = Math.round(minutes / 60);
    if (hours < 24) { return `${hours} hr ago`; }
    return new Date(date).toLocaleDateString();
  }

  ask(question: string): void {
    this.duesy.send(question);
    this.autoScroll = true;
    this.input?.nativeElement.focus();
  }

  submit(): void {
    const text = this.draft.trim();
    if (!text || this.state.sending || text.length > this.state.maxMessageLength) { return; }
    this.draft = '';
    this.ask(text);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.submit();
    }
  }

  onEscape(event: Event): void {
    event.stopPropagation();
    this.closeRequest.emit();
  }

  newChat(): void {
    this.duesy.newChat();
    this.draft = '';
    this.input?.nativeElement.focus();
  }

  retry(message: DuesyMessage): void {
    this.autoScroll = true;
    this.duesy.retry(message.localId);
  }

  onScroll(): void {
    const el = this.scroller?.nativeElement;
    if (!el) { return; }
    this.autoScroll = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_SLACK_PX;
    if (this.autoScroll) { this.hasUnseen = false; }
  }

  jumpToLatest(): void {
    this.autoScroll = true;
    this.hasUnseen = false;
    this.scrollToBottom();
    this.input?.nativeElement.focus();
  }

  toggleMic(): void {
    if (this.listening) {
      this.recognition?.stop();
      return;
    }
    const w = window as any;
    const Recognition = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!Recognition) { return; }
    this.recognition = new Recognition();
    this.recognition.lang = navigator.language || 'en-US';
    this.recognition.interimResults = false;
    this.recognition.onresult = (event: any) =>
      this.zone.run(() => {
        const transcript = Array.from(event.results as ArrayLike<any>).map((r: any) => r[0].transcript).join(' ');
        this.draft = `${this.draft ? this.draft + ' ' : ''}${transcript}`.slice(0, this.state.maxMessageLength);
      });
    this.recognition.onend = () => this.zone.run(() => (this.listening = false));
    this.recognition.onerror = () => this.zone.run(() => (this.listening = false));
    this.listening = true;
    this.recognition.start();
  }

  private lastContent(state: DuesyState): string {
    const last = state.messages[state.messages.length - 1];
    return last ? `${last.localId}:${last.status}:${last.content.length}` : '';
  }

  private afterContentChange(): void {
    if (this.autoScroll) {
      this.cdr.detectChanges();
      this.scrollToBottom();
    } else {
      this.hasUnseen = true;
    }
  }

  private scrollToBottom(): void {
    const el = this.scroller?.nativeElement;
    if (el) { el.scrollTop = el.scrollHeight; }
  }

  ngOnDestroy(): void {
    this.recognition?.abort?.();
    if (this.clock) { clearInterval(this.clock); }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
